import { useCallback, useContext, useId, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import type { Chat } from '@trinity/chat';
import { ChatActionType } from '@trinity/chat';
import { SubmitActionContext } from '@trinity/chat-components/components/Chat/context';
import { AnalysisQueryHint } from '../presentation/Message.QueryHint/QueryHint';

type Props = {
  response: Chat.QueryResponsePlannerClarify;
  // True once a later message exists: the question can no longer be answered.
  disabled: boolean;
};

/**
 * The analytic planner's clarifying questions. The user picks one option
 * per question; the answers go back with the checkpoint ID and version the
 * server sent, and the server checks them against the paused turn.
 */
export const PlannerClarify = ({ response, disabled }: Props) => {
  const submitAction = useContext(SubmitActionContext);
  const idBase = useId();
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const { questions } = response.data;

  const allAnswered = questions.every((q) => chosen[q.id] !== undefined);
  const locked = disabled || submitted;

  const summary = useMemo(
    () =>
      questions
        .map((q) => q.options.find((o) => o.id === chosen[q.id])?.label)
        .filter((label): label is string => label !== undefined)
        .join('; '),
    [questions, chosen]
  );

  const handleContinue = useCallback(() => {
    if (!allAnswered || locked) return;
    setSubmitted(true);
    submitAction?.(
      {
        type: ChatActionType.PlannerClarifyReply,
        payload: {
          checkpointId: response.data.checkpointId,
          checkpointVersion: response.data.checkpointVersion,
          answers: questions.map((q) => ({ questionId: q.id, optionId: chosen[q.id] ?? '' })),
        },
      },
      summary
    );
  }, [allAnswered, locked, submitAction, response.data, questions, chosen, summary]);

  return (
    <Root>
      {questions.map((question, index) => (
        <Question key={question.id}>
          <Prompt id={`${idBase}-prompt-${index}`}>{question.prompt}</Prompt>
          <RadioGroup
            aria-labelledby={`${idBase}-prompt-${index}`}
            value={chosen[question.id] ?? ''}
            onChange={(_, optionId) => setChosen((current) => ({ ...current, [question.id]: optionId }))}
          >
            {question.options.map((option) => (
              <FormControlLabel
                key={option.id}
                value={option.id}
                control={<Radio disabled={locked} />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </Question>
      ))}
      <Actions>
        <AnalysisQueryHint value="Continue" disabled={!allAnswered || locked} onClick={handleContinue} />
      </Actions>
    </Root>
  );
};

const Root = styled('div')({
  boxSizing: 'border-box',
});

const Question = styled('div')({
  '&:not(:last-of-type)': {
    marginBottom: 16,
  },
});

const Prompt = styled('div')(({ theme }) => ({
  fontSize: 15,
  fontFamily: theme.fonts.semiBold,
  marginBottom: 6,
}));

const Actions = styled('div')({
  marginTop: 12,
});
